import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Skeleton,
  Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import { buscarAuditoriasPedidoReembolso } from '../../../services/pedidoReembolsoService';
import type { PedidoReembolsoAuditoria } from '../../../types/pedidoReembolso';

export interface AuditoriaReembolsoDialogProps {
  open: boolean;
  pedidoReembolsoId: string;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  valorTotal: 'Valor Total',
  invoiceId: 'Invoice ID',
  motivoSolicitacao: 'Motivo da Solicitacao',
  motivoRecusa: 'Motivo da Recusa',
  observacaoOrganizador: 'Observacao do Organizador',
  meioPagamentoOriginal: 'Meio de Pagamento',
  iuguRefundId: 'ID do Refund (Iugu)',
  criadoEm: 'Criado em',
  atualizadoEm: 'Atualizado em',
};

const formatValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (key === 'valorTotal' && typeof value === 'number') {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  if ((key === 'criadoEm' || key === 'atualizadoEm') && typeof value === 'string') {
    return dayjs(value).format('DD/MM/YYYY HH:mm');
  }
  return String(value);
};

const getChangedFields = (
  antes: Record<string, unknown> | null | undefined,
  depois: Record<string, unknown> | null | undefined,
): { campo: string; de: string; para: string }[] => {
  if (!depois) return [];
  const changes: { campo: string; de: string; para: string }[] = [];
  const keys = new Set([...Object.keys(antes || {}), ...Object.keys(depois)]);
  keys.forEach((key) => {
    if (key === 'id') return;
    const valorAntes = antes ? antes[key] : undefined;
    const valorDepois = depois[key];
    if (JSON.stringify(valorAntes) !== JSON.stringify(valorDepois)) {
      changes.push({
        campo: FIELD_LABELS[key] || key,
        de: formatValue(key, valorAntes),
        para: formatValue(key, valorDepois),
      });
    }
  });
  return changes;
};

const AuditoriaReembolsoDialog: React.FC<AuditoriaReembolsoDialogProps> = ({
  open,
  pedidoReembolsoId,
  onClose,
}) => {
  const [auditorias, setAuditorias] = useState<PedidoReembolsoAuditoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !pedidoReembolsoId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await buscarAuditoriasPedidoReembolso(pedidoReembolsoId);
        if (!cancelled) setAuditorias(result);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Erro ao carregar historico de auditoria.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, pedidoReembolsoId]);

  const parseDados = (dados: unknown): Record<string, unknown> | null => {
    if (!dados) return null;
    if (typeof dados === 'string') {
      try { return JSON.parse(dados); } catch { return null; }
    }
    return dados as Record<string, unknown>;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historico de Auditoria</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" flexDirection="column" gap={2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={80} />
            ))}
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && auditorias.length === 0 && (
          <Typography color="text.secondary">Nenhum registro de auditoria encontrado.</Typography>
        )}
        {!loading && !error && auditorias.map((audit, index) => {
          const dadosAnteriores = parseDados(audit.dadosAnteriores);
          const dadosNovos = parseDados(audit.dadosNovos);
          const isCriado = audit.acao === 'CRIADO';
          const changes = isCriado ? [] : getChangedFields(dadosAnteriores, dadosNovos);

          return (
            <Box key={audit.id} mb={index < auditorias.length - 1 ? 2 : 0}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {isCriado ? 'Pedido Criado' : 'Pedido Alterado'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {audit.realizadoEm ? dayjs(audit.realizadoEm).format('DD/MM/YYYY HH:mm') : '-'}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={1}>
                Por: {audit.realizadoPor?.nome || 'Sistema'}
              </Typography>

              {isCriado && dadosNovos && (
                <Box sx={{ pl: 2, borderLeft: '3px solid #2e7d32' }}>
                  <>
                    <Typography variant="body2">
                      {'Status: '}<strong>{String(dadosNovos.status || '-')}</strong>
                    </Typography>
                    {dadosNovos.valorTotal != null && (
                      <Typography variant="body2">
                        {'Valor: '}<strong>{formatValue('valorTotal', dadosNovos.valorTotal)}</strong>
                      </Typography>
                    )}
                    {dadosNovos.motivoSolicitacao && (
                      <Typography variant="body2">
                        {'Motivo: '}<strong>{String(dadosNovos.motivoSolicitacao)}</strong>
                      </Typography>
                    )}
                  </>
                </Box>
              )}

              {!isCriado && changes.length > 0 && (
                <Box sx={{ pl: 2, borderLeft: '3px solid #0288d1' }}>
                  {changes.map((change, ci) => (
                    <Typography key={ci} variant="body2">
                      {change.campo}: <span style={{ color: '#d32f2f' }}>{change.de}</span>
                      {' → '}
                      <span style={{ color: '#2e7d32' }}>{change.para}</span>
                    </Typography>
                  ))}
                </Box>
              )}

              {!isCriado && changes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                  Nenhuma alteracao detectada nos campos rastreados.
                </Typography>
              )}

              {index < auditorias.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditoriaReembolsoDialog;
